// The message a member pastes into the unit's channel.
//
// One message, both surfaces: the extension's `MUQ_FORMAT.discordMessage` is
// this function, bundled into `extension-mu/format.js`. It was a hand-written
// mirror until the build existed, and the two had already drifted — the
// extension gated the ready message on a contract-outlook status where this one
// compares queued against remaining.
//
// This is the biggest parity gap after change-commitment: the person on a phone
// who was mentioned could not re-muster anybody. The board also knows its own
// link, so unlike the panel it needs no round trip to include one.

import type { MuState } from "@shared/types";
import { noContractVerdict, unitWideLine } from "./copy";

const num = (n: number) => Math.round(Number(n) || 0).toLocaleString("en-US");

interface Row {
  username: string;
  discordId: string | null;
  online: boolean;
  alreadyHitThisRound: boolean;
  committed: number;
  delivered: number;
  owed: number;
}

/** An account id is the only form that notifies; a name renders as text and
 * pings nobody, which is what this used to do for everyone. */
export const discordMention = (r: Row) => (r.discordId ? `<@${r.discordId}>` : `@${r.username}`);

export function queueRows(state: MuState): Row[] {
  const byId = new Map(state.roster.map((m) => [m.userId, m]));
  return [...state.queue]
    .sort((a, b) => a.slot - b.slot)
    .map((q) => {
      const m = byId.get(q.userId);
      const pledged = typeof q.pledgedDamage === "number" && q.pledgedDamage > 0;
      const committed = pledged ? q.pledgedDamage! : (m?.projectedDamage ?? 0);
      const delivered = m?.contractDamage ?? 0;
      return {
        username: q.username,
        discordId: m?.discordId ?? null,
        online: m?.online ?? false,
        alreadyHitThisRound: m?.alreadyHitThisRound ?? false,
        committed,
        delivered,
        // A pledge counts down as it is delivered; a projection is recomputed
        // from health every poll and already excludes what hitting has spent,
        // so subtracting again would charge the member twice.
        owed: pledged ? Math.max(0, committed - delivered) : committed,
      };
    });
}

export function discordSummary(state: MuState, boardUrl: string | null = null): string {
  const live = Boolean(state.contract && state.progress);
  const lines = [`**${state.muName} contract queue**`];
  const rows = queueRows(state);
  if (live && state.contract && state.progress) {
    lines.push(
      `${num(state.progress.totalDamage)} / ${num(state.contract.targetDamage)} damage (${num(state.progress.remainingDamage)} to go)`,
    );
  } else {
    // A standing queue holds a real figure against nothing yet, and saying only
    // "no contract" threw away the half that decides whether to go bidding.
    lines.push(noContractVerdict(rows.reduce((sum, r) => sum + r.committed, 0)));
  }
  // Ruling 6: with more than one contract being fulfilled, coverage per
  // contract can read covered on each while the unit is short overall. The
  // channel is exactly where that gets misread, so the total travels with it.
  const wide = state.unitWide ? unitWideLine(state.unitWide) : null;
  if (wide) lines.push(wide);
  if (rows.length === 0) lines.push("Queue is empty.");
  rows.forEach((r, i) => {
    const marks = [r.online ? "online" : "offline"];
    if (r.alreadyHitThisRound) marks.push("already hit");
    const figure = live
      ? `${num(r.delivered)} dealt, ${num(r.owed)} to go`
      : `${num(r.committed)} projected`;
    lines.push(`${i + 1}. ${r.username} - ${figure} (${marks.join(", ")})`);
  });
  // The point of pasting this is to move the people who have not finished.
  // Tagging somebody who is done only trains them to ignore it.
  if (live) {
    const owing = rows.filter((r) => r.owed > 0).map(discordMention);
    if (owing.length > 0) lines.push(`**Still owed:** ${owing.join(" ")}`);
  }
  if (boardUrl) lines.push(`**Open the board:** ${boardUrl}`);
  return lines.join("\n");
}

export function discordReadyMessage(state: MuState): string | null {
  if (!state.contract || !state.progress) return null;
  const target = state.contract.targetDamage;
  const remaining = Math.max(0, target - state.progress.totalDamage);
  if (remaining <= 0) return null;
  const rows = queueRows(state);
  const queued = rows.reduce((sum, r) => sum + r.owed, 0);
  if (queued < remaining) return null;
  const mentions = rows
    .filter((r) => r.owed > 0)
    .map(discordMention)
    .join(" ");
  if (!mentions) return null;
  return `${mentions}\nContract damage: ${num(target)}. Projected queue damage: ${num(queued)}.\nEnough damage is queued to cover the ${num(remaining)} remaining damage.`;
}

export function contractWonMessage(state: MuState, boardUrl: string | null = null): string | null {
  if (!state.contract?.confirmed) return null;
  const rows = queueRows(state);
  const ready = rows.reduce((sum, r) => sum + r.owed, 0);
  const target = state.contract.targetDamage;
  const lines = [`**Contract won** - ${state.muName}`, `${num(target)} damage needed.`];
  if (rows.length === 0) {
    lines.push("Nobody is registered. Open the board and say you are in.");
  } else {
    lines.push(
      `${rows.length} ${rows.length === 1 ? "member registered" : "members registered"}, ${num(ready)} damage between them${ready < target ? ` - still ${num(target - ready)} short.` : "."}`,
    );
    lines.push(rows.map(discordMention).join(" "));
  }
  if (boardUrl) lines.push(`**Open the board:** ${boardUrl}`);
  return lines.join("\n");
}

/** A contract nobody has hit yet is news: that is the moment the unit has to be
 * moved, and it outranks the standing summary. */
export function discordMessage(state: MuState, boardUrl: string | null = null): string {
  if (state.phase === "won") return contractWonMessage(state, boardUrl) ?? discordSummary(state, boardUrl);
  return discordReadyMessage(state) ?? discordSummary(state, boardUrl);
}
